import { useEffect, useRef, useState } from 'react';
import type { PointerEvent as ReactPointerEvent } from 'react';

const clamp = (v: number, min: number, max: number) => Math.max(min, Math.min(max, v));
const MARGIN = 8;
const SIZE = 64; // alto/ancho aprox del botón, para no salirse de la pantalla

type Pos = { x: number; y: number };

/**
 * Hace un elemento FLOTANTE arrastrable. Devuelve la posición (left/top en px), los
 * handlers de pointer y `wasDragged()` para distinguir un clic de un arrastre (así el
 * onClick no se dispara al soltar tras mover). La posición se guarda en localStorage.
 * `size` (opcional) es el ancho/alto del elemento para no dejarlo salir de la pantalla
 * (por defecto el del botón). Útil para paneles grandes como la ventana del chat.
 */
export function useDraggable(
  storageKey: string,
  defaultPos: (vw: number, vh: number) => Pos,
  size?: { w: number; h: number }
) {
  const sw = size?.w ?? SIZE;
  const sh = size?.h ?? SIZE;
  const clampToView = (p: Pos): Pos => ({
    x: clamp(p.x, MARGIN, Math.max(MARGIN, window.innerWidth - sw)),
    y: clamp(p.y, MARGIN, Math.max(MARGIN, window.innerHeight - sh)),
  });

  const [pos, setPos] = useState<Pos>(() => {
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved) return clampToView(JSON.parse(saved));
    } catch {
      /* ignore */
    }
    return clampToView(defaultPos(window.innerWidth, window.innerHeight));
  });

  const drag = useRef<{ sx: number; sy: number; ox: number; oy: number; moved: boolean } | null>(null);
  const movedRef = useRef(false);

  // Reacomodar si la ventana cambia de tamaño (no quedar fuera de pantalla).
  useEffect(() => {
    const onResize = () => setPos((p) => clampToView(p));
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const onPointerDown = (e: ReactPointerEvent) => {
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    drag.current = { sx: e.clientX, sy: e.clientY, ox: pos.x, oy: pos.y, moved: false };
    movedRef.current = false;
  };
  const onPointerMove = (e: ReactPointerEvent) => {
    if (!drag.current) return;
    const dx = e.clientX - drag.current.sx;
    const dy = e.clientY - drag.current.sy;
    if (Math.abs(dx) > 5 || Math.abs(dy) > 5) {
      drag.current.moved = true;
      movedRef.current = true;
    }
    setPos(clampToView({ x: drag.current.ox + dx, y: drag.current.oy + dy }));
  };
  const onPointerUp = () => {
    if (drag.current?.moved) {
      try {
        localStorage.setItem(storageKey, JSON.stringify(pos));
      } catch {
        /* ignore */
      }
    }
    drag.current = null;
  };

  return {
    pos,
    dragHandlers: { onPointerDown, onPointerMove, onPointerUp },
    /** true si el último gesto fue un arrastre (para suprimir el onClick). */
    wasDragged: () => movedRef.current,
  };
}
